import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { countryAPI } from '../../services/api';
import SuccessModal from '../Common/SuccessModal';
import ErrorModal from '../Common/ErrorModal';
import { AdminPage, StateBlock } from '../Common/admin';

const CountryEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    countryCode: '',
    nameAr: '',
    nameEng: '',
    currencyId: '',
    timeZone: '',
    idNationalLength: '',
    mobileNumberLength: '',
    isActive: true,
    isArabicCountry: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const loadCountry = useCallback(async () => {
    try {
      setLoadingData(true);
      const response = await countryAPI.getById(id);
      const data = response.data;
      setFormData({
        ...data,
        currencyId: data.currencyId || '',
        idNationalLength: data.idNationalLength || '',
        mobileNumberLength: data.mobileNumberLength || '',
        isActive: !!data.isActive,
        isArabicCountry: !!data.isArabicCountry,
      });
    } catch (err) {
      setErrorMessage(t('country.edit.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoadingData(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadCountry();
  }, [loadCountry]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.countryCode.trim()) {
      newErrors.countryCode = t('country.edit.errors.countryCodeRequired');
    }
    if (!formData.nameAr.trim()) {
      newErrors.nameAr = t('country.edit.errors.nameArRequired');
    }
    if (!formData.nameEng.trim()) {
      newErrors.nameEng = t('country.edit.errors.nameEngRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      await countryAPI.update(id, {
        ...formData,
        currencyId: formData.currencyId ? parseInt(formData.currencyId) : 0,
        idNationalLength: formData.idNationalLength ? parseInt(formData.idNationalLength) : 0,
        mobileNumberLength: formData.mobileNumberLength
          ? parseInt(formData.mobileNumberLength)
          : 0,
      });
      setShowSuccessModal(true);
      setTimeout(() => navigate('/country'), 1000);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        setErrorMessage(t('country.edit.messages.updateError'));
        setShowErrorModal(true);
      }
      setLoading(false);
    }
  };

  const field = (name, label, { type = 'text', required = false, maxLength } = {}) => (
    <div className="ad-form-field">
      <label className={`ad-label ${required ? 'ad-label-required' : ''}`} htmlFor={name}>
        {label}
      </label>
      <input
        id={name}
        type={type}
        name={name}
        className={`ad-input ${errors[name] ? 'ad-input-error' : ''}`}
        value={formData[name] ?? ''}
        onChange={handleChange}
        maxLength={maxLength}
      />
      {errors[name] && <span className="ad-error-text">{errors[name]}</span>}
    </div>
  );

  const backButton = (
    <button type="button" className="ad-btn" onClick={() => navigate('/country')}>
      <i className="fas fa-arrow-right"></i>
      {t('country.edit.backToList')}
    </button>
  );

  if (loadingData) {
    return (
      <AdminPage title={t('country.edit.title')} headerActions={backButton}>
        <StateBlock icon="fa-hourglass-half" title={t('common.loading')} />
      </AdminPage>
    );
  }

  return (
    <AdminPage title={t('country.edit.title')} headerActions={backButton}>
      <form onSubmit={handleSubmit}>
        <div className="ad-form">
          <div className="ad-form-grid">
            {field('countryCode', t('country.edit.fields.countryCode'), {
              required: true,
              maxLength: 10,
            })}
            {field('nameAr', t('country.edit.fields.nameAr'), {
              required: true,
              maxLength: 100,
            })}
            {field('nameEng', t('country.edit.fields.nameEng'), {
              required: true,
              maxLength: 100,
            })}
            {field('currencyId', t('country.edit.fields.currency'), { type: 'number' })}
            {field('timeZone', t('country.edit.fields.timeZone'), { maxLength: 50 })}
            {field('idNationalLength', t('country.edit.fields.idNationalLength'), {
              type: 'number',
            })}
            {field('mobileNumberLength', t('country.edit.fields.mobileNumberLength'), {
              type: 'number',
            })}
          </div>

          <div className="ad-form-section">
            <div className="ad-check-row">
              <input
                id="isActive"
                type="checkbox"
                name="isActive"
                className="ad-checkbox"
                checked={formData.isActive}
                onChange={handleChange}
              />
              <label htmlFor="isActive">{t('country.edit.fields.isActive')}</label>
            </div>
            <div className="ad-check-row">
              <input
                id="isArabicCountry"
                type="checkbox"
                name="isArabicCountry"
                className="ad-checkbox"
                checked={formData.isArabicCountry}
                onChange={handleChange}
              />
              <label htmlFor="isArabicCountry">
                {t('country.edit.fields.isArabicCountry')}
              </label>
            </div>
          </div>
        </div>

        <div className="ad-form-foot">
          <button type="button" className="ad-btn" onClick={() => navigate('/country')}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="ad-btn ad-btn-primary" disabled={loading}>
            {loading ? t('common.loading') : t('country.edit.save')}
          </button>
        </div>
      </form>

      <SuccessModal
        show={showSuccessModal}
        message={t('country.edit.messages.updateSuccess')}
        onClose={() => setShowSuccessModal(false)}
      />
      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </AdminPage>
  );
};

export default CountryEdit;
